import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { IconProps } from '../../assets/icons/icon.types';

import {
  CapQuestLogo,
  CheckCircleIcon,
  PieChartIcon,
  ShareClassesIcon,
  UsersIcon,
} from '../../assets/icons/index';
import TickIcon from '../../assets/icons/tick-icon';
import comfiLogo from '../../assets/images/companies/comfi-logo.svg';
import credibleLogo from '../../assets/images/companies/credible-logo.svg';
import crossvalLogo from '../../assets/images/companies/crossval-logo.svg';
import holoLogo from '../../assets/images/companies/holo-logo.svg';
import letsworkLogo from '../../assets/images/companies/letswork-logo.svg';
import rentifyLogo from '../../assets/images/companies/rentify-logo.svg';
import maskGroupNumber from '../../assets/images/mask-group-number.svg';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { GoogleAuthButton } from '../../components/google-auth-button/index';
import { Input } from '../../components/input/index';
import LineDelimiter from '../../components/line-delimiter/index';
import OrDelimiter from '../../components/or-delimiter/index';
import { BackendRoute } from '../../config/index';
import { HttpExceptionBody } from '../../exceptions/http-exception.util';
import { HttpException } from '../../exceptions/index';
import { useAppMutation, useLocale } from '../../hooks/index';
import { ApiService } from '../../services/index';
import { inviteStakeholderStore } from '../../storage/local/index';
import { AuthProvider } from '../../types/auth.types';
import { TermsRoute } from '../../types/page.types';

type SignupTestimonial = {
  author: string;
  quote: string;
  title: string;
};

const signupTestimonials: SignupTestimonial[] = [
  {
    quote:
      'CapQuest gave us something larger platforms could not - speed, proximity, and a product built around our actual needs. The team stayed incredibly close to our requirements, adapted quickly, and delivered solutions faster than any larger incumbent realistically could.',
    author: 'James Evans',
    title: 'CFO Holo',
  },
  {
    quote:
      'Before CapQuest, our cap table lived across spreadsheets that quickly became difficult to maintain and trust. Today, everything is centralized, accurate, and always up to date, with investors able to clearly understand their ownership and position.',
    author: 'Marwan Hattar',
    title: 'Co-Founder Rawa',
  },
  {
    quote:
      'As we prepared for our next fundraise, CapQuest scenario modeling became an essential planning tool. It allowed us to model different round structures, understand dilution impact across stakeholders, and make more informed decisions before entering investor discussions.',
    author: 'Gary Blowers',
    title: 'CEO Tribe',
  },
  {
    quote:
      'Managing ESOPs at scale had become increasingly manual and difficult to maintain. CapQuest streamlined the entire process - from grants to vesting tracking - giving us a much more automated and reliable system.',
    author: 'Hamsika Gopalan',
    title: 'CFO CredibleX',
  },
];

const SIGNUP_FEATURE_ICON_COLOR = '#475467';

type SignupValuePoint = {
  Icon: React.FC<IconProps>;
  label: string;
};

const signupValuePoints: SignupValuePoint[] = [
  { label: 'Share classes management', Icon: ShareClassesIcon },
  { label: 'Employee plan management', Icon: CheckCircleIcon },
  { label: 'Investor onboarding', Icon: UsersIcon },
  { label: 'Scenario modeling', Icon: PieChartIcon },
];

const trustedCompanyLogos = [
  { alt: 'Holo', src: holoLogo },
  { alt: 'Comfi', src: comfiLogo },
  { alt: 'Rentify', src: rentifyLogo },
  { alt: 'letswork', src: letsworkLogo },
  { alt: 'Crossval', src: crossvalLogo },
  { alt: 'CredibleX', src: credibleLogo },
];
const SIGNUP_TESTIMONIAL_KEY = 'signup-last-testimonial-index';
const testimonialCardShadow =
  'shadow-[0_-4px_16px_0_rgba(16,24,40,0.04),0_24px_48px_-12px_rgba(16,24,40,0.18)]';

const getInitialTestimonialIndex = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const previousRawValue = window.localStorage.getItem(SIGNUP_TESTIMONIAL_KEY);
  const previousIndex = previousRawValue === null ? null : Number(previousRawValue);
  const hasValidPreviousIndex =
    previousIndex !== null &&
    Number.isInteger(previousIndex) &&
    previousIndex >= 0 &&
    previousIndex < signupTestimonials.length;

  const nextIndex = hasValidPreviousIndex ? (previousIndex + 1) % signupTestimonials.length : 0;

  window.localStorage.setItem(SIGNUP_TESTIMONIAL_KEY, String(nextIndex));

  return nextIndex;
};

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [testimonialIndex] = useState(getInitialTestimonialIndex);

  const { locale } = useLocale();
  const [isVerificationLinkSent, setIsVerificationLinkSent] = useState(false);
  const landingBaseUrl = process.env.REACT_APP_LANDING_URL;
  const googleAuthUrl = `${process.env.REACT_APP_BACKEND_URL}/auth/google`;

  const handleSendVerificationLink = async () => {
    await ApiService.post({ endpoint: BackendRoute.VERIFICATION }, { body: { email }, locale });
    setIsVerificationLinkSent(true);
  };

  const handleResendVerificationLink = async () => {
    await ApiService.post(
      { endpoint: BackendRoute.VERIFICATION_REFRESH },
      { body: { email }, locale },
    );
  };

  const verificationLinkMutation = useAppMutation<void, HttpException>([], {
    mutationFn: handleSendVerificationLink,
    defaultErrorHandling: false,
  });

  const verificationRefreshLinkMutation = useAppMutation<void, HttpException>([], {
    mutationFn: handleResendVerificationLink,
    defaultErrorHandling: false,
  });

  useEffect(() => {
    const invitation = inviteStakeholderStore.get();
    if (!invitation) return;

    if (invitation.isRegistered) {
      inviteStakeholderStore.set({ ...invitation, isRegistered: false });
      navigate('/login');
      return;
    }

    setEmail(invitation.email);
  }, [navigate]);

  const error = verificationLinkMutation.error?.body as
    | ({
        error: AuthProvider;
      } & HttpExceptionBody)
    | undefined;

  useEffect(() => {
    if (error) {
      navigate(`/login?email=${encodeURIComponent(email)}`);
    }
  }, [email, error, navigate]);

  const selectedTestimonial = signupTestimonials[testimonialIndex];

  return (
    <div className="flex min-h-screen w-full lg:h-screen">
      <div className="register-panel-background relative hidden min-h-screen min-w-0 overflow-hidden lg:flex lg:w-3/5">
        <img
          alt=""
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 w-[115%] min-w-[1050px] max-w-none -translate-x-1/2 opacity-80"
          src={maskGroupNumber}
        />
        <div className="relative flex min-h-screen w-full items-center justify-center px-[clamp(16px,3%,24px)] py-12 lg:px-[72px]">
          <div className="w-full max-w-[620px]">
            <div className="relative mx-auto w-[480px] pt-12">
              <div
                className={`absolute left-12 top-2 h-[100px] w-[384px] rounded-2xl bg-white/65 ${testimonialCardShadow}`}
              />
              <div
                className={`absolute left-6 top-7 h-[100px] w-[432px] rounded-2xl bg-white/85 ${testimonialCardShadow}`}
              />
              <div
                className={`relative flex h-[232px] w-[480px] flex-col justify-between overflow-hidden rounded-2xl bg-white px-6 py-[22px] ${testimonialCardShadow}`}
              >
                <p className="text-sm leading-5 text-gray-700">"{selectedTestimonial.quote}"</p>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-gray-600">
                    {selectedTestimonial.author}
                  </span>
                  <span className="text-sm text-gray-500">{selectedTestimonial.title}</span>
                </div>
              </div>
            </div>

            <div className="mx-auto mb-10 mt-12 flex w-[500px] flex-col items-center gap-4">
              <div className="flex gap-[24px]">
                {signupValuePoints.slice(0, 2).map(({ label, Icon }) => (
                  <div className="flex items-center gap-3 text-sm text-gray-600" key={label}>
                    <Icon className="size-4 shrink-0" iconColor={SIGNUP_FEATURE_ICON_COLOR} />
                    {label}
                  </div>
                ))}
              </div>
              <div className="flex gap-[24px]">
                {signupValuePoints.slice(2, 4).map(({ label, Icon }) => (
                  <div className="flex items-center gap-3 text-sm text-gray-600" key={label}>
                    <Icon className="size-4 shrink-0" iconColor={SIGNUP_FEATURE_ICON_COLOR} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            <div className="mx-auto w-[480px] border-t border-[#E4E7EC] px-6 py-8">
              <p className="text-center text-xs font-normal text-gray-600">
                <AppFormattedMessage id="TRUSTED_BY_LEADING_COMPANIES" />
              </p>
              <div className="mt-8 grid grid-cols-3 items-center gap-x-6 gap-y-8">
                {trustedCompanyLogos.map(({ alt, src }) => (
                  <div className="flex items-center justify-center px-2 py-3" key={alt}>
                    <img alt={alt} className="h-5 max-w-full object-contain" src={src} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="min-h-screen w-full overflow-y-auto bg-white lg:w-2/5">
        <div className="flex min-h-screen w-full flex-col items-center justify-center px-[clamp(16px,3%,24px)] py-12 lg:px-[72px]">
          <div className="flex w-full max-w-[394px] -translate-y-12 flex-col gap-8">
            <div className="flex flex-col gap-2">
              <div>
                <CapQuestLogo className="size-12" />
              </div>

              <div className="flex flex-col gap-2">
                <div
                  className="!bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
                  style={{
                    background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
                  }}
                >
                  <AppFormattedMessage
                    id={isVerificationLinkSent ? 'CHECK_YOUR_INBOX' : 'GET_STARTED'}
                  />
                </div>
                <div className="text-sm text-gray-500">
                  {isVerificationLinkSent ? (
                    <AppFormattedMessage id="SENDED_LOGIN_VERIFY_LINK" />
                  ) : (
                    'Manage share classes, employee plans, and investor relations with ease.'
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              {!isVerificationLinkSent && (
                <>
                  <a className="block no-underline" href={googleAuthUrl}>
                    <GoogleAuthButton />
                  </a>
                  <OrDelimiter />
                </>
              )}

              <Input
                disabled={isVerificationLinkSent}
                errorMessage={verificationLinkMutation.error?.body?.message}
                icon={isVerificationLinkSent ? <TickIcon /> : undefined}
                isSuccess={isVerificationLinkSent}
                onChange={setEmail}
                placeholder={<AppFormattedMessage id="EMAIL_ADDRESS" />}
                type="email"
                value={email}
              />

              {!isVerificationLinkSent && (
                <Button
                  disabled={!email}
                  isLoading={verificationLinkMutation.isPending}
                  onClick={() => verificationLinkMutation.mutate()}
                >
                  <AppFormattedMessage id="CONTINUE" />
                </Button>
              )}
            </div>

            <div className="font-inter text-[10px] font-medium text-gray-500">
              <AppFormattedMessage id="BY_PROCEEDING_YOU_AGREE_TO" />{' '}
              <button
                className="text-[#2565C8]"
                onClick={() =>
                  window.open(`${landingBaseUrl}${TermsRoute.TERMS_OF_SERVICE}`, '_blank')
                }
              >
                <AppFormattedMessage id="TERMS_OF_SERVICE" />
              </button>{' '}
              <AppFormattedMessage id="AND" />{' '}
              <button
                className="text-[#2565C8]"
                onClick={() =>
                  window.open(`${landingBaseUrl}${TermsRoute.PRIVACY_POLICY}`, '_blank')
                }
              >
                <AppFormattedMessage id="PRIVACY_POLICY" />.
              </button>
            </div>

            {isVerificationLinkSent && (
              <div className="flex flex-col">
                <button
                  className="w-fit font-inter text-sm font-[450] text-[#2565C8] underline"
                  onClick={() => verificationRefreshLinkMutation.mutate()}
                >
                  <AppFormattedMessage id="RESEND_VERIFICATION_EMAIL" />
                </button>
                {verificationRefreshLinkMutation.error && (
                  <div className="text-sm text-fireside-600">
                    {verificationRefreshLinkMutation?.error?.body?.message}
                  </div>
                )}
                {verificationRefreshLinkMutation.isSuccess && (
                  <div className="text-sm text-forest-500">
                    Successfully resent verification link
                  </div>
                )}
              </div>
            )}

            <LineDelimiter />
            <div className="flex items-center gap-2 text-sm">
              <div className="font-[450] text-gray-700">
                <AppFormattedMessage id="ALREADY_HAVE_ACCOUNT" />
              </div>
              <button
                className="font-[550] text-[#2565C8] underline"
                onClick={() => navigate('/login')}
              >
                <AppFormattedMessage id="LOG_IN" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
